import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Components/Icons/Close";
import Button from "../../Subcomponents/Button";
import { errorTost } from "../../Services/Toast";
import { dismissLoader, presentLoader } from "../../Services/loader";
import { startEsign } from "../../Services/AllotmentEsign";
import { sanitizeConsentHtml } from "../PaymentConsent/sanitizeConsentHtml";
import cssClasses from "./AllotmentEsignModal.module.css";

const DEFAULT_TITLE = "Terms and conditions";
const SCROLL_SLACK_PX = 8;
const SCROLL_HINT = "Please scroll to the end of the terms to continue.";
const atBottom = (el) => !!el && el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_SLACK_PX;

const primaryBtn = (disabled) => ({
	width: "fit-content",
	color: "#fff",
	backgroundColor: disabled ? "#B2CCFF" : "#155EEF",
	border: `1px solid ${disabled ? "#B2CCFF" : "#155EEF"}`,
	fontSize: "1.4rem",
	lineHeight: "2rem",
});
const secondaryBtn = {
	width: "fit-content",
	color: "#101828",
	backgroundColor: "#fff",
	border: "1px solid #E6E9EF",
	fontSize: "1.4rem",
	lineHeight: "2rem",
};

// Shows the allotment terms for one signer and sends them to Surepass to Aadhaar-eSign.
// item: one pending item from GET /allotment-esign/pending. Only the first pending item is shown;
// after the customer comes back, the next Pay Now shows the next one.
const AllotmentEsignModal = ({ open, handleClose, allotmentPaymentId, title, item, signerNo = 1, signerCount = 1, summary = [] }) => {
	const [agreed, setAgreed] = useState(false);
	const [starting, setStarting] = useState(false);
	const inFlight = useRef(false); // blocks a double tap before the state update lands
	const termsHtml = useMemo(() => sanitizeConsentHtml(item?.termsHtml), [item?.termsHtml]);
	// "I agree" unlocks once the terms are scrolled to the end (or fit without scrolling)
	const termsRef = useRef(null);
	const [readToEnd, setReadToEnd] = useState(false);
	useEffect(() => {
		// measured after render; the next frame catches late layout (fonts, images)
		if (atBottom(termsRef.current)) return setReadToEnd(true);
		const raf = window.requestAnimationFrame(() => atBottom(termsRef.current) && setReadToEnd(true));
		return () => window.cancelAnimationFrame(raf);
	}, [termsHtml]);

	async function onEsign() {
		if (!agreed || inFlight.current) return;
		inFlight.current = true;
		setStarting(true);
		presentLoader();
		try {
			let url = item?.openEsign?.url;
			if (!url) {
				const body = { allotmentPaymentId };
				if (item?.applicantId) body.applicantId = item.applicantId;
				url = (await startEsign(body))?.url;
			}
			if (!url) throw new Error("Unable to start eSign. Please try again.");
			window.location.assign(url);
			// the page is leaving for Surepass: keep the button disabled
		} catch (err) {
			dismissLoader();
			errorTost(err?.message || "Unable to start eSign. Please try again.");
			inFlight.current = false;
			setStarting(false);
		}
	}

	const disabled = !agreed || starting;

	return (
		<Modal
			open={open}
			onClose={(_, reason) => {
				if (reason !== "backdropClick" && !starting) handleClose();
			}}
			sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }}
			disableAutoFocus={true}
		>
			<Box className={cssClasses.modalWrapper}>
				<div className={cssClasses.wrapperHeader}>
					<div className={cssClasses.titleRow}>
						<h3 className={cssClasses.title}>{title || DEFAULT_TITLE}</h3>
						<span className={cssClasses.closeIcon} onClick={() => !starting && handleClose()} tabIndex={0} aria-label="Close">
							<Close width={"1.2rem"} height={"1.2rem"} />
						</span>
					</div>
					{signerCount > 1 && <div className={cssClasses.signerCount}>{`Signer ${signerNo} of ${signerCount}`}</div>}
				</div>

				<div className={cssClasses.wrapperContent}>
					{summary.length > 0 && (
						<dl className={cssClasses.summary}>
							{summary.map(([label, value]) => (
								<React.Fragment key={label}>
									<dt>{label}</dt>
									<dd>{value}</dd>
								</React.Fragment>
							))}
						</dl>
					)}
					<div
						ref={termsRef}
						className={cssClasses.termsBox}
						tabIndex={0}
						onScroll={(e) => !readToEnd && atBottom(e.currentTarget) && setReadToEnd(true)}
					>
						<div dangerouslySetInnerHTML={{ __html: termsHtml }} />
					</div>
					<p className={cssClasses.signerLine}>
						{`To be signed by ${item?.signerName || "-"} with Aadhaar (mobile ${item?.maskedMobile || "-"})`}
					</p>
					<label className={cssClasses.agreeRow}>
						<input type="checkbox" checked={agreed} disabled={starting || !readToEnd} onChange={(e) => setAgreed(e.target.checked)} />
						<span>I agree</span>
					</label>
					{!readToEnd && <p className={cssClasses.scrollHint}>{SCROLL_HINT}</p>}
				</div>
				<div className={cssClasses.wrapperFooter}>
					<Button btnName={"Cancel"} clicked={() => !starting && handleClose()} style={secondaryBtn} />
					<Button
						btnName={"I agree & eSign with Aadhaar"}
						clicked={onEsign}
						disable={disabled}
						isLoading={starting}
						style={primaryBtn(disabled)}
					/>
				</div>
			</Box>
		</Modal>
	);
};

export default AllotmentEsignModal;
